import { FC, useMemo } from 'react';

import { EnquiryList } from '@shared/api/enquiry/enquiry.interface';
import { THEME } from '@shared/state/atom/theme.atom';
import { mainBgColorGenerator } from '@shared/utils/mainColorGenerator';
import { Table, TableColumnsType } from 'antd';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';

import EnquiryDetailModalOpener from '../EnquiryDetailModal/EnquiryDetailModalOpener';

interface IProps {
  enquiryList: EnquiryList;
  searchValue: string;
}

type EnquiryTableData = {
  id: string;
  user: string;
  phoneNumber: string;
  status: string;
  //* 답변 문자 발송 여부 (Done인데 없으면 도입 전 답변·처리 중·실패)
  smsSent: boolean;
};

const EnquiryListTable: FC<IProps> = ({ enquiryList, searchValue }: IProps) => {
  const customTheme = THEME;

  const searchedEnquiryList = useMemo(() => {
    const searcher = new FuzzySearch(enquiryList, ['user.info.name', 'name']);
    return searcher.search(searchValue);
  }, [enquiryList, searchValue]);

  const tableData: EnquiryTableData[] = useMemo(() => {
    return _.map(
      searchedEnquiryList as EnquiryList | null,
      (enquiry): EnquiryTableData => {
        //* 비로그인(화환 스토어) 문의는 user가 null이라 폼에 적은 이름·번호로 폴백
        return {
          id: enquiry._id,
          user: enquiry.user?.info?.name || enquiry.name || '-',
          phoneNumber: enquiry.user?.info?.phoneNumber || enquiry.phoneNumber || '-',
          status: enquiry.status,
          smsSent: !!enquiry.answerNotifiedAt,
        };
      },
    );
  }, [searchedEnquiryList]);

  const columns: TableColumnsType<EnquiryTableData> = [
    {
      width: '30%',
      title: '성함',
      dataIndex: 'user',
      key: 'user',
      align: 'center',
      render: (name) => {
        return <p className="whitespace-pre-line break-keep text-xs">{name}</p>;
      },
    },
    {
      width: '32%',
      title: '연락처',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      align: 'center',
      render: (phoneNumber) => {
        return <p className="whitespace-pre-line break-keep text-xs">{phoneNumber}</p>;
      },
    },
    {
      width: '20%',
      title: '처리여부',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status, row) => {
        return (
          <div className="space-y-0.5">
            <p className="whitespace-pre-line break-keep text-xs">{status}</p>
            {row.smsSent && <p className="text-[10px] text-green-600">문자발송</p>}
          </div>
        );
      },
    },
    {
      width: '13%',
      key: 'enquiry-detail-modal-button',
      align: 'center',
      render: (value: EnquiryTableData) => {
        return (
          <div className="center-box">
            <EnquiryDetailModalOpener
              enquiry={enquiryList.find((enquiry) => enquiry._id === value.id) ?? null}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        className="w-full"
        columns={columns}
        dataSource={_.flatMapDeep([tableData])}
        pagination={{ position: ['bottomCenter'], pageSize: 15 }}
        rowKey={'id'}
        size={'small'}
        onHeaderRow={() => ({
          className: `text-xs font-bold bg-opacity-40 ${mainBgColorGenerator(
            customTheme,
          )}`,
        })}
      />
    </div>
  );
};

export default EnquiryListTable;
